import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, CheckCircle, HelpCircle, KeyRound, AlertCircle, Mail, Search } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/hooks/useAuth";
import { getSecurityQuestions } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Email Recovery Sub-component ---
function PurchaseRecovery({ onFound }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLookup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/lookup-purchase`, { email });
      if (res.data.status === "found") {
        onFound(res.data.session_id);
      } else if (res.data.status === "already_registered") {
        setError(res.data.message + " — redirecting to login...");
        setTimeout(() => window.location.href = "/login", 2000);
      } else {
        setError(res.data.message);
      }
    } catch {
      setError("Something went wrong. Please try again or contact support.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-blue-500/15 flex items-center justify-center mx-auto mb-4">
            <Search size={30} className="text-blue-400" />
          </div>
          <h1 className="text-2xl font-extrabold text-white mb-2">Recover Your Purchase</h1>
          <p className="text-slate-400 text-sm">
            Enter the email address you used during checkout and we'll pick up where you left off.
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-slate-800/50 p-6 shadow-xl">
          {error && (
            <div className="flex items-center gap-2 mb-4 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle size={14} className="text-red-400" />
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}
          <form onSubmit={handleLookup} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">Purchase Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input
                  data-testid="recovery-email-input"
                  type="email"
                  placeholder="The email used at checkout"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-10 h-11 rounded-md border bg-slate-900/50 border-slate-700/50 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600 focus:outline-none text-sm"
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="recovery-submit"
              disabled={loading}
              className="w-full h-11 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2 text-sm"
            >
              {loading ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : "Find My Purchase"}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-600 mt-4">
          Still stuck? Email us at <span className="text-slate-400">DocRVUTracker@gmail.com</span>
        </p>
      </div>
    </div>
  );
}

export default function CompleteRegistration() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [sessionId, setSessionId] = useState(searchParams.get("session_id"));

  const [sessionInfo, setSessionInfo] = useState(null);
  const [sessionError, setSessionError] = useState("");
  const [loadingSession, setLoadingSession] = useState(true);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Load session info and security questions in parallel
  useEffect(() => {
    if (!sessionId) {
      setSessionError("No payment session found. Please complete checkout first.");
      setLoadingSession(false);
      return;
    }

    Promise.all([
      axios.get(`${API}/subscription/session-info/${sessionId}`),
      getSecurityQuestions(),
    ])
      .then(([sessionRes, questionsRes]) => {
        setSessionInfo(sessionRes.data);
        setSecurityQuestions(questionsRes.questions || []);
      })
      .catch(() => {
        setSessionError("Could not verify your payment. Please contact support if you were charged.");
      })
      .finally(() => setLoadingSession(false));
  }, [sessionId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!selectedQuestion || !securityAnswer.trim()) {
      setError("Please select a security question and provide an answer for password recovery");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/complete-registration`, {
        session_id: sessionId,
        password,
        security_question: selectedQuestion,
        security_answer: securityAnswer,
      });
      login(res.data, false);
      navigate("/rvu-tracker");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // No session_id in URL — show recovery flow
  if (!sessionId) {
    return <PurchaseRecovery onFound={(sid) => setSessionId(sid)} />;
  }

  if (loadingSession) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-blue-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Payment Verification Failed</h2>
          <p className="text-slate-400 text-sm mb-6">{sessionError}</p>
          <a href="/register" className="text-blue-400 hover:text-blue-300 text-sm font-medium">← Back to Registration</a>
        </div>
      </div>
    );
  }

  const planLabels = { monthly: "Monthly Subscription", yearly: "Annual Subscription", lifetime: "Lifetime Access" };

  return (
    <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-400" />
          </div>
          <h1 className="text-2xl font-extrabold text-white mb-1">Payment Successful!</h1>
          <p className="text-slate-400 text-sm">
            {sessionInfo?.is_trial
              ? "Your 7-day free trial is active."
              : `Your ${planLabels[sessionInfo?.plan_id] || "subscription"} is active.`}
            {" "}Now set up your account password to access the tracker.
          </p>
        </div>

        {/* Plan badge */}
        {sessionInfo && (
          <div className="mb-6 rounded-xl bg-white/5 border border-white/10 px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold">Account Email</p>
              <p className="text-white font-medium text-sm mt-0.5">{sessionInfo.email}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold">Plan</p>
              <p className="text-green-400 font-semibold text-sm mt-0.5">
                {sessionInfo.is_trial ? "Free Trial" : sessionInfo.plan_name}
              </p>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="rounded-2xl border border-white/10 bg-slate-800/50 backdrop-blur-sm p-6 shadow-xl">
          <h2 className="text-lg font-semibold text-white mb-5">Set Your Password</h2>

          {error && (
            <div className="flex items-center gap-2 mb-4 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle size={14} className="text-red-400" />
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input
                  data-testid="complete-reg-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Min. 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 h-11 rounded-md border bg-slate-900/50 border-slate-700/50 hover:border-slate-600 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-0 text-sm"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input
                  data-testid="complete-reg-confirm-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Repeat your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 h-11 rounded-md border bg-slate-900/50 border-slate-700/50 hover:border-slate-600 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-0 text-sm"
                />
              </div>
            </div>

            {/* Security question */}
            <div className="space-y-3 pt-1">
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">
                  Security Question <span className="text-slate-600">(for password recovery)</span>
                </label>
                <div className="relative">
                  <HelpCircle className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <select
                    data-testid="complete-reg-security-question"
                    value={selectedQuestion}
                    onChange={(e) => setSelectedQuestion(e.target.value)}
                    className="w-full pl-10 pr-4 h-11 rounded-md border appearance-none cursor-pointer bg-slate-900/50 border-slate-700/50 hover:border-slate-600 focus:border-blue-500/50 text-slate-200 focus:outline-none text-sm"
                  >
                    <option value="">Select a security question...</option>
                    {securityQuestions.map((q, idx) => (
                      <option key={idx} value={q}>{q}</option>
                    ))}
                  </select>
                </div>
              </div>
              {selectedQuestion && (
                <div>
                  <label className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">Your Answer</label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <input
                      data-testid="complete-reg-security-answer"
                      type="text"
                      placeholder="Enter your answer..."
                      value={securityAnswer}
                      onChange={(e) => setSecurityAnswer(e.target.value)}
                      className="w-full pl-10 h-11 rounded-md border bg-slate-900/50 border-slate-700/50 hover:border-slate-600 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600 focus:outline-none text-sm"
                    />
                  </div>
                  <p className="text-xs text-slate-600 mt-1">Remember this answer — you'll need it to reset your password</p>
                </div>
              )}
            </div>

            <button
              type="submit"
              data-testid="complete-reg-submit"
              disabled={loading}
              className="w-full h-11 mt-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2 text-sm"
            >
              {loading ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                "Create My Account & Access Tracker"
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-600 mt-4">Your payment is secured by Stripe. Your data is private and secure.</p>
      </div>
    </div>
  );
}
